import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function playAlert() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
    g.gain.setValueAtTime(0.18, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.7);
    o.start();
    o.stop(ctx.currentTime + 0.7);
    o.onended = () => ctx.close();
  } catch {}
}

const STATUSES = ["new", "contacted", "enrolled", "archived"];
const STATUS_COLOR = {
  new: "text-[#00E5FF]",
  contacted: "text-yellow-400",
  enrolled: "text-green-400",
  archived: "text-white/40",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderCount, setOrderCount] = useState(0);
  const [wishlists, setWishlists] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const prevCountRef = useRef(null);
  const soundRef = useRef(true);

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const [e, s, o, w, wl] = await Promise.all([
        api.get("/admin/enrollments"),
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/wishlists"),
        api.get("/admin/waitlist"),
      ]);
      setRows(e.data);
      setStats(s.data);
      setOrderCount(o.data.length);
      setWishlists(w.data);
      setWaitlist(wl.data);
      const total = (s.data.new || 0) + o.data.length + w.data.length + wl.data.length;
      if (prevCountRef.current !== null && total > prevCountRef.current) {
        toast.success("New request received!", {
          description: "A client just applied or ordered. Check the table below.",
        });
        if (soundRef.current) playAlert();
      }
      prevCountRef.current = total;
    } catch {
      navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user && user.role) load();
  }, [user, load]);

  useEffect(() => {
    if (!user || !user.role) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user, load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enrollments/${id}`, { status });
      setRows((r) => r.map((x) => (x._id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (user === null || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <span className="text-xs uppercase tracking-[0.3em] text-white/40">Loading…</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/10 bg-black/70 px-6 py-4 backdrop-blur-xl md:px-10">
        <Link to="/" className="font-display text-2xl tracking-wide">
          DELTA<span className="text-[#7C3AED]">DL</span>
          <span className="ml-3 text-xs uppercase tracking-[0.3em] text-white/40">Admin</span>
        </Link>
        <div className="flex items-center gap-4">
          <button
            data-testid="admin-alerts-toggle"
            onClick={() =>
              setSoundOn((v) => {
                soundRef.current = !v;
                return !v;
              })
            }
            className="relative rounded-full border border-white/20 p-2.5 transition-colors hover:bg-white/10"
            aria-label="Toggle request alerts"
            title={soundOn ? "Sound alerts on" : "Sound alerts off"}
          >
            {soundOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4 text-white/40" />}
            {((stats?.new || 0) + orderCount + wishlists.length + waitlist.length) > 0 && (
              <span
                data-testid="admin-alerts-badge"
                className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#7C3AED] px-1 text-[10px] font-bold text-white"
              >
                {(stats?.new || 0) + orderCount + wishlists.length + waitlist.length}
              </span>
            )}
          </button>
          <span className="hidden text-sm text-white/50 md:block">{user.email}</span>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => {
              await logout();
              navigate("/admin/login");
            }}
            className="rounded-full border border-white/20 px-5 py-2 text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 py-10 md:px-10">
        <h1 className="font-display text-5xl uppercase tracking-tight md:text-7xl">
          Applications
        </h1>

        {/* Stats */}
        {stats && (
          <div
            data-testid="admin-stats"
            className="mt-10 grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4 lg:grid-cols-7"
          >
            <Stat label="Total" value={stats.total} />
            <Stat label="New" value={stats.new} accent />
            {Object.entries(stats.by_course).map(([k, v]) => (
              <Stat key={k} label={k} value={v} />
            ))}
          </div>
        )}

        {/* Table */}
        <div className="mt-12 overflow-x-auto border border-white/10">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-[0.2em] text-white/40">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Program</th>
                <th className="px-5 py-4">Message</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody data-testid="admin-enrollments-table">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-white/40">
                    No applications yet.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r._id}
                  data-testid={`admin-row-${r._id}`}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <td className="px-5 py-4 font-medium">{r.name}</td>
                  <td className="px-5 py-4 text-white/60">
                    <div>{r.email}</div>
                    {r.phone && <div className="text-white/40">{r.phone}</div>}
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-display uppercase text-base">{r.course}</span>
                  </td>
                  <td className="px-5 py-4 max-w-xs text-white/50">
                    {r.message || <span className="text-white/20">—</span>}
                  </td>
                  <td className="px-5 py-4 text-white/40 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        data-testid={`admin-status-trigger-${r._id}`}
                        className={`text-xs uppercase tracking-widest ${STATUS_COLOR[r.status] || "text-white"}`}
                      >
                        {r.status} ▾
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#111] border-white/10 text-white">
                        {STATUSES.map((s) => (
                          <DropdownMenuItem
                            key={s}
                            data-testid={`admin-status-${s}-${r._id}`}
                            onClick={() => changeStatus(r._id, s)}
                            className="uppercase text-xs tracking-widest focus:bg-[#7C3AED] focus:text-white"
                          >
                            {s}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {wishlists.length > 0 && (
          <section data-testid="admin-wishlists" className="mt-10">
            <h2 className="font-display text-2xl tracking-wide text-white">Wishlist Requests</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {wishlists.map((w) => (
                <div
                  key={w.id}
                  data-testid={`admin-wishlist-${w.id}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-white">{w.name || "Client"}</p>
                      <a className="text-xs text-[#7C3AED] hover:underline" href={`mailto:${w.email}`}>
                        {w.email}
                      </a>
                    </div>
                    <span className="text-xs text-white/40">
                      {w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <ul className="mt-3 space-y-1 text-sm text-white/60">
                    {w.items.map((i, idx) => (
                      <li key={idx} className="flex justify-between gap-2">
                        <span className="line-clamp-1">{i.title || i.category}</span>
                        <span>{i.price ? `$${i.price.toFixed(2)}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}
        {waitlist.length > 0 && (
          <section data-testid="admin-waitlist" className="mt-10">
            <h2 className="font-display text-2xl tracking-wide text-white">Waiting List</h2>
            <p className="mt-1 text-xs text-white/40">
              Clients waiting for checkout to open — email them when payments go live.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {waitlist.map((w) => (
                <div
                  key={w.id}
                  data-testid={`admin-waitlist-${w.id}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-white">{w.name || "Client"}</p>
                      <a className="text-xs text-[#7C3AED] hover:underline" href={`mailto:${w.email}`}>
                        {w.email}
                      </a>
                    </div>
                    <span className="text-xs text-white/40">
                      {w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                  {w.interest && (
                    <p className="mt-3 text-sm text-white/70">
                      <span className="text-white/40">Interested in:</span> {w.interest}
                    </p>
                  )}
                  {w.message && (
                    <p className="mt-1 text-sm text-white/50 line-clamp-2">{w.message}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-[#050505] px-5 py-6">
    <div className={`font-display text-4xl ${accent ? "text-[#7C3AED]" : "text-white"}`}>
      {value}
    </div>
    <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</div>
  </div>
);
